/**
 * @fileoverview Entry point for the Rithmo Meeting Moderation React application.
 *
 * This file initializes the React application and mounts it to the DOM.
 * It imports global styles and wraps the App component in React.StrictMode
 * for development-time checks.
 *
 * @module index
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/critical.css';
import './styles/global.css';

/**
 * Error Boundary to catch rendering errors
 */
type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Rithmo] React Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ color: '#333', marginBottom: '8px' }}>Something went wrong</h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #0b6fa4, #16c1b2)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Create the React root and render the application.
 * The app is mounted to the #root element in react-app.html.
 */
try {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element not found');
  }
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
  console.log('[Rithmo] React app mounted successfully');
} catch (error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  console.error('[Rithmo] Failed to mount React app:', message);
  // Show a basic error message if React fails to mount
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw error;
  }
  rootEl.innerHTML = `
    <div style="padding: 40px 20px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
      <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
      <h2 style="color: #333; margin-bottom: 8px;">Failed to load app</h2>
      <p style="color: #666; font-size: 14px;">${message}</p>
      <button onclick="window.location.reload()" style="margin-top: 16px; padding: 10px 20px; background: linear-gradient(135deg, #0b6fa4, #16c1b2); color: #fff; border: none; border-radius: 6px; cursor: pointer;">
        Retry
      </button>
    </div>
  `;
}
